import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import { useState } from 'react';
import { ENTITY_STATUSES, statusMeta } from './entityConstants';

// Create / edit one compartment (entity) in the tenant tree.
// A "booking node" issues engagements and must carry an entity_code (the confirmation
// prefix); a "folder" just groups and skips the code. Parent options exclude the entity
// itself and its descendants (no cycles).
function EntityFormModal({ mode = 'create', entity = null, entities = [], descendantIds = [], onClose, onSave, theme = 'light' }) {
    const dark = theme === 'dark';
    const isEdit = mode === 'edit';

    const [form, setForm] = useState({
        name: entity?.name || '',
        level_type: entity?.level_type || (entities.find((item) => item.id === entity?.parent_entity_id)?.level_type === 'company' ? 'market' : 'company'),
        display_label: entity?.display_label || '',
        parent_entity_id: entity?.parent_entity_id || (isEdit ? '' : entities.find((item) => item.level_type === 'organization')?.id) || '',
        is_booking_node: entity?.is_booking_node || false,
        entity_code: entity?.entity_code || '',
        status: entity?.status || 'active'
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    
    // Valid parents: any entity except self + its descendants (prevents cycles).
    const parentOptions = entities.filter((e) => e.id !== entity?.id && !descendantIds.includes(e.id)
        && e.level_type === (form.level_type === 'market' ? 'company' : 'organization'));
    const levelLabel = form.level_type === 'market' ? 'Market' : form.level_type === 'organization' ? 'Organization' : 'Company';

    const inputCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
    }`;
    const labelCls = `block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`;

    const handleSubmit = async () => {
        setError(null);
        if (!form.name.trim()) { setError('Name is required.'); return; }
        if (!form.entity_code.trim()) {
            setError('Enter a code for this Company/Market (the confirmation-number prefix when bookings are enabled).');
            return;
        }
        if (!/^[A-Z0-9]{2,5}$/.test(form.entity_code.trim().toUpperCase())) {
            setError('Code must be 2-5 uppercase letters/digits.');
            return;
        }
        if (entities.some((item) => item.id !== entity?.id && item.entity_code === form.entity_code.trim().toUpperCase())) {
            setError('This code is already used in your organization.');
            return;
        }
        if (form.level_type !== 'organization' && !parentOptions.some((item) => item.id === form.parent_entity_id)) {
            setError(form.level_type === 'market' ? 'A Market must sit under a Company.' : 'A Company must sit under the Organization.');
            return;
        }
        // color_palette is deliberately absent — Branding owns it now, and omitting it here means
        // an edit to structure/numbering never disturbs a compartment's colour.
        const payload = {
            name: form.name.trim(),
            level_type: form.level_type,
            display_label: form.display_label.trim() || null,
            parent_entity_id: form.parent_entity_id || null,
            is_booking_node: form.is_booking_node,
            entity_code: form.entity_code.trim().toUpperCase(),
            status: form.status
        };
        try {
            setSaving(true);
            await onSave(payload);
        } catch (e) {
            setError(e?.message || 'Something went wrong while saving.');
            setSaving(false);
        }
    };

    return (
        <ModalSurface closeOnEscape={false} onClose={onClose} label="Company or market" overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" panelClassName={`rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto ${dark ? 'bg-gray-800' : 'bg-white'}`} panelProps={{"data-m6-layout":"entities","data-m6-component":"EntityFormModal"}}>
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>
                        {isEdit ? `Edit ${levelLabel}` : `New ${levelLabel}`}
                    </h2>
                    <button onClick={onClose} className={`ia-modal-desktop-close text-2xl ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>×</button>
                </div>

                <div className="p-6 space-y-5">
                    {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelCls}>Name <span className="required-asterisk">*</span></label>
                            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. The Ride, Atlanta" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Structure Level</label>
                            <select value={form.level_type} disabled={isEdit && entity?.level_type === 'organization'}
                                onChange={(e) => setForm((prev) => ({ ...prev, level_type: e.target.value, parent_entity_id: '' }))} className={inputCls}>
                                {entity?.level_type === 'organization' && <option value="organization">Organization</option>}
                                <option value="company">Company</option><option value="market">Market</option>
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelCls}>{form.level_type === 'market' ? 'Company' : 'Organization'}</label>
                            <select value={form.parent_entity_id} disabled={form.level_type === 'organization'} onChange={(e) => set('parent_entity_id', e.target.value)} className={inputCls}>
                                <option value="">Choose a parent</option>
                                {parentOptions.map((e) => (
                                    <option key={e.id} value={e.id}>{e.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className={`flex items-center gap-3 ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
                                <input type="checkbox" checked={form.is_booking_node} onChange={(e) => set('is_booking_node', e.target.checked)} className="w-5 h-5 text-orange-600 rounded" />
                                <span className="text-sm font-medium">Front desk — issues engagements (booking node)</span>
                            </label>
                            <p className={`text-xs mt-1 ml-8 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Off = this Company/Market does not take bookings directly.</p>
                        </div>

                        {
                            <div>
                                <label className={labelCls}>{levelLabel} Code <span className="required-asterisk">*</span></label>
                                <input type="text" value={form.entity_code} onChange={(e) => set('entity_code', e.target.value.toUpperCase())} placeholder="ATL" maxLength={5} className={`${inputCls} font-mono uppercase`} />
                                <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>2-5 uppercase letters/digits; unique within your organization. Used for new employee IDs and confirmations; existing IDs never change.</p>
                            </div>
                        }

                        <div>
                            <label className={labelCls}>Status</label>
                            <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls}>
                                {ENTITY_STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                            </select>
                        </div>

                        {/* Brand colour is NOT edited here anymore. It moved to Settings › Organization ›
                            Branding, where you see the result on a real button and menu before saving
                            (Val, 2026-07-17). Keeping a second colour picker in this form was the "why two
                            settings?" duplication — a wheel with no preview, right next to the real one. This
                            form now owns STRUCTURE and NUMBERING only; it never touches color_palette, so a
                            compartment's colour survives an edit here untouched. */}
                        <div className="md:col-span-2">
                            <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                                🎨 Set this Company/Market's <strong>brand colour</strong> under <strong>Structure → Branding</strong>.
                            </p>
                        </div>
                    </div>
                </div>

                <div className={`sticky bottom-0 border-t px-6 py-4 flex justify-end gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <button onClick={onClose} disabled={saving} className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}>Cancel</button>
                    <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50">
                        {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${levelLabel}`}
                    </button>
                </div>
            </ModalSurface>
    );
}

export default EntityFormModal;
